import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAlert } from "react-alert";
import { updateDoc, doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import "./HappyHourSetupModal.scss";
import closeIcon from "../../images/CloseCircle.svg";
import deleteIcon from "../../images/DeleteIcon.svg";
import dayjs from "dayjs";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function HappyHourSetupModal({ isOpen, onClose, selectedOrg }) {
  const { t } = useTranslation("manage");
  const alert = useAlert();

  const [formData, setFormData] = useState({
    isActive: false,
    date: dayjs().format("YYYY-MM-DD"),
    startTime: null,
    endTime: null,
    discountType: "individual",
    globalDiscountPercentage: "",
    productDiscounts: [],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [discountSearchTerm, setDiscountSearchTerm] = useState("");
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchHappyHourData = async () => {
      if (isOpen && selectedOrg?.id) {
        try {
          const orgDocRef = doc(db, "Organizations", selectedOrg.id);
          const orgDoc = await getDoc(orgDocRef);

          if (orgDoc.exists()) {
            const orgData = orgDoc.data();
            const existingData = orgData.happyHour;

            if (existingData) {
              const date = existingData.date || dayjs().format("YYYY-MM-DD");
              setFormData({
                isActive: existingData.isActive || false,
                date,
                startTime: existingData.startTime
                  ? dayjs(`${date} ${existingData.startTime}`).toDate()
                  : null,
                endTime: existingData.endTime
                  ? dayjs(`${date} ${existingData.endTime}`).toDate()
                  : null,
                discountType: existingData.discountType || "individual",
                globalDiscountPercentage: existingData.globalDiscountPercentage || "",
                productDiscounts: existingData.productDiscounts || [],
              });
            } else {
              setFormData({
                isActive: false,
                date: dayjs().format("YYYY-MM-DD"),
                startTime: null,
                endTime: null,
                discountType: "individual",
                globalDiscountPercentage: "",
                productDiscounts: [],
              });
            }
          }
        } catch (error) {
          console.error("Error fetching happy hour data:", error);
          setFormData({
            isActive: false,
            date: dayjs().format("YYYY-MM-DD"),
            startTime: null,
            endTime: null,
            discountType: "individual",
            globalDiscountPercentage: "",
            productDiscounts: [],
          });
        }
      } else if (isOpen) {
        setFormData({
          isActive: false,
          date: dayjs().format("YYYY-MM-DD"),
          startTime: null,
          endTime: null,
          discountType: "individual",
          globalDiscountPercentage: "",
          productDiscounts: [],
        });
      }
      setErrors({});
    };

    const fetchProducts = async () => {
      if (isOpen && selectedOrg?.id) {
        try {
          const q = query(collection(db, "products"), where("orgId", "==", selectedOrg.id));
          const querySnapshot = await getDocs(q);
          const productsData = [];
          querySnapshot.forEach((doc) => {
            productsData.push({ id: doc.id, ...doc.data() });
          });
          setProducts(productsData);
        } catch (error) {
          console.error("Error fetching products:", error);
          setProducts([]);
        }
      }
    };

    fetchHappyHourData();
    fetchProducts();
  }, [isOpen, selectedOrg?.id]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".searchable-dropdown")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.date) {
      newErrors.date = t("date_required");
    }

    if (!formData.startTime) {
      newErrors.startTime = t("start_time_required");
    }

    if (!formData.endTime) {
      newErrors.endTime = t("end_time_required");
    }

    if (formData.startTime && formData.endTime && formData.startTime >= formData.endTime) {
      newErrors.endTime = t("end_time_must_be_after_start");
    }

    if (formData.discountType === "global") {
      if (!formData.globalDiscountPercentage || formData.globalDiscountPercentage === "") {
        newErrors.globalDiscountPercentage = t("global_percentage_required");
      } else {
        const percentage = parseInt(formData.globalDiscountPercentage);
        if (isNaN(percentage) || percentage < 1 || percentage > 100) {
          newErrors.globalDiscountPercentage = t("global_percentage_must_be_between_1_and_100");
        }
      }
    }

    return newErrors;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleDiscountTypeChange = (newDiscountType) => {
    setFormData((prev) => ({
      ...prev,
      discountType: newDiscountType,
      globalDiscountPercentage: newDiscountType === "global" ? prev.globalDiscountPercentage : "",
      productDiscounts: newDiscountType === "individual" ? prev.productDiscounts : [],
    }));

    setErrors((prev) => ({
      ...prev,
      globalDiscountPercentage: null,
      productDiscounts: null,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!selectedOrg?.id) {
      alert.show(t("organization_not_selected"), {
        type: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const happyHourData = {
        isActive: formData.isActive,
        date: formData.date ? dayjs(formData.date).format("YYYY-MM-DD") : null,
        startTime: formData.startTime ? dayjs(formData.startTime).format("HH:mm") : null,
        endTime: formData.endTime ? dayjs(formData.endTime).format("HH:mm") : null,
        discountType: formData.discountType,
        globalDiscountPercentage:
          formData.discountType === "global" ? parseInt(formData.globalDiscountPercentage) : null,
        productDiscounts: formData.discountType === "individual" ? formData.productDiscounts : [],
      };

      const orgDocRef = doc(db, "Organizations", selectedOrg.id);
      await updateDoc(orgDocRef, {
        happyHour: happyHourData,
      });

      alert.show(t("happy_hour_updated"), {
        type: "success",
      });
      handleClose();
    } catch (error) {
      alert.show(t("happy_hour_not_updated"), {
        type: "error",
      });
      console.error("Error updating happy hour:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      isActive: false,
      date: dayjs().format("YYYY-MM-DD"),
      startTime: null,
      endTime: null,
      discountType: "individual",
      globalDiscountPercentage: "",
      productDiscounts: [],
    });
    setErrors({});
    setDiscountSearchTerm("");
    setDropdownSearchTerm("");
    setIsDropdownOpen(false);
    onClose();
  };

  const handleProductDiscountChange = (productId, discountPercentage) => {
    const value = discountPercentage.trim();

    setFormData((prev) => {
      const newProductDiscounts = [...prev.productDiscounts];
      const existingIndex = newProductDiscounts.findIndex((pd) => pd.productId === productId);

      if (value === "" || value === "0") {
        if (existingIndex !== -1) {
          newProductDiscounts[existingIndex] = { productId, discountPercentage: "" };
        }
      } else {
        const discount = parseInt(value);
        if (discount >= 1 && discount <= 100) {
          if (existingIndex !== -1) {
            newProductDiscounts[existingIndex] = { productId, discountPercentage: discount };
          } else {
            newProductDiscounts.push({ productId, discountPercentage: discount });
          }
        }
      }

      return {
        ...prev,
        productDiscounts: newProductDiscounts,
      };
    });
  };

  const handleRemoveProductDiscount = (productId) => {
    setFormData((prev) => ({
      ...prev,
      productDiscounts: prev.productDiscounts.filter((pd) => pd.productId !== productId),
    }));
  };

  const handleAddProductDiscount = (productId) => {
    if (!formData.productDiscounts.find((pd) => pd.productId === productId)) {
      setFormData((prev) => ({
        ...prev,
        productDiscounts: [...prev.productDiscounts, { productId, discountPercentage: 10 }],
      }));
    }
  };

  const getAvailableProducts = () => {
    const availableProducts = products.filter(
      (product) => !formData.productDiscounts.find((pd) => pd.productId === product.id),
    );

    if (dropdownSearchTerm) {
      return availableProducts.filter(
        (product) =>
          product.title.toLowerCase().includes(dropdownSearchTerm.toLowerCase()) ||
          product.price.toString().includes(dropdownSearchTerm),
      );
    }

    return availableProducts;
  };

  const getFilteredDiscounts = () => {
    if (!discountSearchTerm) {
      return formData.productDiscounts;
    }

    return formData.productDiscounts.filter((discount) => {
      const product = products.find((p) => p.id === discount.productId);
      if (!product) return false;

      return (
        product.title.toLowerCase().includes(discountSearchTerm.toLowerCase()) ||
        product.price.toString().includes(discountSearchTerm)
      );
    });
  };

  const handleDropdownProductSelect = (productId) => {
    handleAddProductDiscount(productId);
    setDropdownSearchTerm("");
    setIsDropdownOpen(false);
  };

  const handleDropdownInputChange = (value) => {
    setDropdownSearchTerm(value);
    setIsDropdownOpen(true);
  };

  if (!isOpen) return null;

  return (
    <div className="happy-hour-modal-overlay" onClick={handleOverlayClick}>
      <div className="happy-hour-modal">
        <div className="modal-header">
          <h3>{t("happy_hour_setup")}</h3>
          <button className="close-btn" onClick={handleClose}>
            <img src={closeIcon} alt={t("close")} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="happy-hour-active">{t("active")}</label>
              <select
                id="happy-hour-active"
                value={formData.isActive}
                onChange={(e) => handleInputChange("isActive", e.target.value === "true")}
              >
                <option value={false}>{t("inactive")}</option>
                <option value={true}>{t("active")}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="happy-hour-date">{t("date")}</label>
              <DatePicker
                id="happy-hour-date"
                selected={dayjs(formData.date).toDate()}
                onChange={(date) => handleInputChange("date", dayjs(date))}
                dateFormat="dd/MM/yyyy"
                autoComplete="off"
                onChangeRaw={(e) => e.preventDefault()}
                placeholderText="DD/MM/YYYY"
                className={errors.date ? "error" : ""}
              />
              {errors.date && <span className="error-text">{errors.date}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="happy-hour-start-time">{t("start_time")}</label>
                <DatePicker
                  id="happy-hour-start-time"
                  selected={formData.startTime}
                  onChange={(time) => handleInputChange("startTime", time)}
                  showTimeSelect
                  showTimeSelectOnly
                  autoComplete="off"
                  onChangeRaw={(e) => e.preventDefault()}
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  placeholderText="HH:MM"
                  className={errors.startTime ? "error" : ""}
                />
                {errors.startTime && <span className="error-text">{errors.startTime}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="happy-hour-end-time">{t("end_time")}</label>
                <DatePicker
                  id="happy-hour-end-time"
                  selected={formData.endTime}
                  onChange={(time) => handleInputChange("endTime", time)}
                  showTimeSelect
                  showTimeSelectOnly
                  autoComplete="off"
                  onChangeRaw={(e) => e.preventDefault()}
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="HH:mm"
                  timeFormat="HH:mm"
                  placeholderText="HH:MM"
                  className={errors.endTime ? "error" : ""}
                />
                {errors.endTime && <span className="error-text">{errors.endTime}</span>}
              </div>
            </div>

            <div className="form-group">
              <label>{t("product_discounts")}</label>

              <div className="discount-type-selection">
                <div className="form-row">
                  <label>
                    <input
                      type="radio"
                      name="discountType"
                      value="individual"
                      checked={formData.discountType === "individual"}
                      onChange={(e) => handleDiscountTypeChange(e.target.value)}
                    />
                    <span>{t("select_product_to_discount")}</span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="discountType"
                      value="global"
                      checked={formData.discountType === "global"}
                      onChange={(e) => handleDiscountTypeChange(e.target.value)}
                    />
                    <span>{t("apply_to_all_products")}</span>
                  </label>
                </div>
                <p className="discount-description">
                  {formData.discountType === "global"
                    ? t("global_discount_description")
                    : t("select_individual_products_for_discounts")}
                </p>
              </div>

              {formData.discountType === "global" ? (
                <div className="global-discount-section">
                  <div className="form-group">
                    <label htmlFor="global-discount-percentage">
                      {t("global_discount_percentage")}
                    </label>
                    <div className="discount-input">
                      <input
                        id="global-discount-percentage"
                        type="number"
                        min="1"
                        max="100"
                        value={formData.globalDiscountPercentage}
                        onChange={(e) =>
                          handleInputChange("globalDiscountPercentage", e.target.value)
                        }
                        placeholder="10"
                        className={errors.globalDiscountPercentage ? "error" : ""}
                      />
                      <span className="percent-symbol">%</span>
                    </div>
                    {errors.globalDiscountPercentage && (
                      <span className="error-text">{errors.globalDiscountPercentage}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="individual-discounts-section">
                  {formData.productDiscounts?.length > 0 && (
                    <>
                      <div className="search-input-container">
                        <input
                          type="text"
                          placeholder={t("search_discounts")}
                          value={discountSearchTerm}
                          onChange={(e) => setDiscountSearchTerm(e.target.value)}
                          className="search-input"
                        />
                      </div>
                      <div className="product-discounts-form">
                        {getFilteredDiscounts().map((discount) => {
                          const product = products.find((p) => p.id === discount.productId);
                          if (!product) return null;

                          return (
                            <div key={discount.productId} className="product-discount-row">
                              <div className="product-info">
                                <span className="product-name">{product.title}</span>
                                <span className="product-price">{product.price}€</span>
                              </div>
                              <div className="discount-input">
                                <input
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={discount.discountPercentage}
                                  onChange={(e) =>
                                    handleProductDiscountChange(discount.productId, e.target.value)
                                  }
                                  placeholder="10"
                                />
                                <span className="percent-symbol">%</span>
                                <button
                                  type="button"
                                  className="remove-discount-btn"
                                  onClick={() => handleRemoveProductDiscount(discount.productId)}
                                  title={t("remove_discount")}
                                >
                                  <img src={deleteIcon} alt={t("remove_discount")} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                  {products.filter(
                    (p) => !formData.productDiscounts.find((pd) => pd.productId === p.id),
                  ).length > 0 && (
                    <div className="add-discount-section">
                      <div className="searchable-dropdown">
                        <input
                          type="text"
                          placeholder={t("search_and_select_product")}
                          value={dropdownSearchTerm}
                          onChange={(e) => handleDropdownInputChange(e.target.value)}
                          onFocus={() => setIsDropdownOpen(true)}
                          className="searchable-dropdown-input"
                        />
                        {isDropdownOpen && getAvailableProducts().length > 0 && (
                          <div className="dropdown-list">
                            {getAvailableProducts().map((product) => (
                              <div
                                key={product.id}
                                className="dropdown-item"
                                onClick={() => handleDropdownProductSelect(product.id)}
                              >
                                <span className="product-name">{product.title}</span>
                                <span className="product-price">{product.price}€</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {errors.productDiscounts && (
                    <span className="error-text">{errors.productDiscounts}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="cancel-btn" onClick={handleClose}>
              {t("cancel")}
            </button>
            <button type="submit" className="primary-btn" disabled={loading}>
              {loading ? t("saving") : t("save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
